import BASE_URL from "../../config/api";
import axios from "axios";
import { useState, useEffect, useMemo } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Link } from "react-router-dom";
import { BsPencil, BsTrash, BsSearch, BsX } from "react-icons/bs";

const AllProducts = () => {
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState({});
  const [drawings, setDrawings] = useState([]);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");

  const runOnStart = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/drawing/get-drawings`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("userToken")}` },
      });
      setDrawings(response.data.data ?? []);
    } catch {
      toast.error("Failed to load drawings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { runOnStart(); }, []);

  // Unique types and categories derived from loaded drawings
  const types = useMemo(() => [...new Set(drawings.map((d) => d.type).filter(Boolean))].sort(), [drawings]);
  const categories = useMemo(() => {
    const source = typeFilter ? drawings.filter((d) => d.type === typeFilter) : drawings;
    return [...new Set(source.map((d) => d.category).filter(Boolean))].sort();
  }, [drawings, typeFilter]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return drawings.filter((d) => {
      const matchesSearch = !q ||
        d.description?.toLowerCase().includes(q) ||
        d.refNo?.toLowerCase().includes(q);
      const matchesType = !typeFilter || d.type === typeFilter;
      const matchesCategory = !categoryFilter || d.category === categoryFilter;
      return matchesSearch && matchesType && matchesCategory;
    });
  }, [drawings, search, typeFilter, categoryFilter]);

  const clearFilters = () => { setSearch(""); setTypeFilter(""); setCategoryFilter(""); };
  const hasActiveFilters = search || typeFilter || categoryFilter;

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this drawing? This cannot be undone.")) return;
    setDeleteLoading((p) => ({ ...p, [id]: true }));
    try {
      const res = await axios.delete(`${BASE_URL}/drawing/delete-drawing/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("userToken")}` },
      });
      if (res.status === 200) {
        toast.success("Drawing deleted");
        setTimeout(runOnStart, 1500);
      }
    } catch {
      toast.error("Failed to delete drawing");
    } finally {
      setDeleteLoading((p) => ({ ...p, [id]: false }));
    }
  };

  return (
    <div>
      <ToastContainer />

      {/* Page header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">All Drawings</h1>
          <p className="text-gray-500 text-sm mt-1">
            {filtered.length} of {drawings.length} plan{drawings.length !== 1 ? "s" : ""}
          </p>
        </div>
        <Link
          to="/dashboard"
          className="bg-[#1877F2] hover:bg-[#0e5fc7] text-white text-sm font-bold px-5 py-2.5 rounded-full transition-colors duration-200"
        >
          + Add Drawing
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <BsSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or ref…"
            className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#1877F2]/20 focus:border-[#1877F2]"
          />
        </div>

        {/* Type */}
        <select
          value={typeFilter}
          onChange={(e) => { setTypeFilter(e.target.value); setCategoryFilter(""); }}
          className="px-4 py-2.5 text-sm border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#1877F2]/20 focus:border-[#1877F2] text-gray-700"
        >
          <option value="">All Types</option>
          {types.map((t) => (
            <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
          ))}
        </select>

        {/* Category — only shown when a type is selected */}
        {typeFilter && (
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-4 py-2.5 text-sm border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#1877F2]/20 focus:border-[#1877F2] text-gray-700"
          >
            <option value="">All Categories</option>
            {categories.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        )}

        {/* Clear */}
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="flex items-center gap-1.5 px-4 py-2.5 text-sm text-gray-500 hover:text-red-500 border border-gray-200 hover:border-red-200 rounded-xl bg-white transition-colors"
          >
            <BsX size={16} /> Clear
          </button>
        )}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 animate-pulse">
              <div className="h-48 bg-gray-200" />
              <div className="p-4 space-y-2">
                <div className="h-3 bg-gray-200 rounded w-3/4" />
                <div className="h-3 bg-gray-200 rounded w-1/2" />
                <div className="h-8 bg-gray-200 rounded mt-3" />
              </div>
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-24 text-gray-400">
          <p className="text-5xl mb-4">🏠</p>
          <p className="text-lg font-semibold">{hasActiveFilters ? "No drawings match your filters" : "No drawings found"}</p>
          {hasActiveFilters ? (
            <button onClick={clearFilters} className="mt-4 text-[#1877F2] underline text-sm font-medium">Clear filters</button>
          ) : (
            <Link to="/dashboard" className="mt-4 inline-block text-[#1877F2] underline text-sm font-medium">Add your first drawing</Link>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {filtered.map((drawing, i) => (
            <div key={i} className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 flex flex-col">
              <div className="relative h-48 flex-shrink-0 overflow-hidden bg-gray-100">
                <img
                  src={drawing.frontElevation}
                  alt={drawing.description || "Drawing"}
                  className="w-full h-full object-cover"
                />
                {drawing.refNo && (
                  <span className="absolute top-2 left-2 bg-white/90 backdrop-blur-sm text-[#1877F2] text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide shadow-sm">
                    {drawing.refNo}
                  </span>
                )}
              </div>
              <div className="p-4 flex flex-col flex-1">
                <p className="font-semibold text-gray-800 text-sm line-clamp-2 flex-1 uppercase leading-snug">
                  {drawing.description}
                </p>
                <p className="text-[#1877F2] font-bold text-sm mt-1">
                  ₦{(Number((drawing.price || "").toString().replace(/[^0-9.]/g, "")) || 0).toLocaleString()}
                </p>
                <div className="flex gap-2 mt-3">
                  <Link
                    to={`/all-drawings/edit/${drawing._id}`}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 bg-gray-100 hover:bg-[#1877F2]/10 text-gray-600 hover:text-[#1877F2] text-xs font-semibold rounded-xl border border-gray-200 hover:border-[#1877F2]/30 transition-colors duration-200"
                  >
                    <BsPencil size={12} /> Edit
                  </Link>
                  <button
                    onClick={() => handleDelete(drawing._id)}
                    disabled={deleteLoading[drawing._id]}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 bg-gray-100 hover:bg-red-50 text-gray-600 hover:text-red-500 text-xs font-semibold rounded-xl border border-gray-200 hover:border-red-200 transition-colors duration-200 disabled:opacity-50"
                  >
                    <BsTrash size={12} /> {deleteLoading[drawing._id] ? "Deleting…" : "Delete"}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AllProducts;
